/* ═══════════════════════════════════════════════════════════
   STORAGE CONTEXT — Per-user storage quota tracking
   Tracks bytes used vs quota, enforces limits before upload.
   ═══════════════════════════════════════════════════════════ */
import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { sbQuery } from "../config/supabase";

/* ── Storage limits by plan ──────────────────────────────── */
export const STORAGE_LIMITS = {
  free_student:    50 * 1024 * 1024,         // 50 MB
  free_tutor:      100 * 1024 * 1024,        // 100 MB
  premium_student: 5 * 1024 * 1024 * 1024,   // 5 GB
  premium_tutor:   5 * 1024 * 1024 * 1024,   // 5 GB
};

/* ── Purchasable storage packages ────────────────────────── */
export const STORAGE_PACKAGES = [
  { id: "1gb",  bytes: 1 * 1024 * 1024 * 1024, slp: 30,  label: "1 GB Extra" },
  { id: "3gb",  bytes: 3 * 1024 * 1024 * 1024, slp: 75,  label: "3 GB Extra" },
  { id: "5gb",  bytes: 5 * 1024 * 1024 * 1024, slp: 120, label: "5 GB Extra", popular: true },
];

/* ── Format bytes to human-readable ──────────────────────── */
export function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

const StorageContext = createContext({
  bytesUsed: 0,
  quotaBytes: 52428800,
  usagePct: 0,
  loading: false,
  refreshUsage: () => {},
  checkUploadAllowed: () => ({ allowed: true, remaining: 0 }),
  recordUpload: () => {},
  setCredentials: () => {},
});

export function useStorage() {
  return useContext(StorageContext);
}

export function StorageProvider({ children }) {
  const [bytesUsed, setBytesUsed] = useState(0);
  const [quotaBytes, setQuotaBytes] = useState(52428800); // 50MB default
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(null);
  const userIdRef = useRef(null);

  const usagePct = quotaBytes > 0
    ? Math.min(100, Math.round((bytesUsed / quotaBytes) * 100))
    : 0;

  const refreshUsage = useCallback(async () => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) return;

    try {
      const rows = await sbQuery("storage_usage", t, "GET", null,
        `?user_id=eq.${u}&select=bytes_used,quota_bytes`);
      const row = rows?.[0];
      if (row) {
        setBytesUsed(row.bytes_used || 0);
        setQuotaBytes(row.quota_bytes || 52428800);
      }
    } catch {
      // Table may not exist — fail silently
    }
  }, []);

  const setCredentials = useCallback((token, userId) => {
    tokenRef.current = token;
    userIdRef.current = userId;
    if (token && userId) {
      refreshUsage();
    } else {
      setBytesUsed(0);
      setQuotaBytes(52428800);
    }
  }, [refreshUsage]);

  const checkUploadAllowed = useCallback((fileSize) => {
    const remaining = quotaBytes - bytesUsed;
    return {
      allowed: fileSize <= remaining,
      remaining,
    };
  }, [bytesUsed, quotaBytes]);

  const recordUpload = useCallback(async (fileSize) => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) return;

    const newUsed = bytesUsed + fileSize;
    setBytesUsed(newUsed);

    try {
      // Upsert storage usage
      await sbQuery("storage_usage", t, "POST", {
        user_id: u,
        bytes_used: newUsed,
        quota_bytes: quotaBytes,
        updated_at: new Date().toISOString(),
      });
    } catch {
      // Try PATCH if row already exists
      try {
        await sbQuery("storage_usage", t, "PATCH", {
          bytes_used: newUsed,
          updated_at: new Date().toISOString(),
        }, `?user_id=eq.${u}`);
      } catch {}
    }
  }, [bytesUsed, quotaBytes]);

  return (
    <StorageContext.Provider value={{
      bytesUsed, quotaBytes, usagePct, loading,
      refreshUsage, checkUploadAllowed, recordUpload, setCredentials,
    }}>
      {children}
    </StorageContext.Provider>
  );
}
